/**
 * Predictive search for the Bunchberry header.
 *
 * Queries Shopify's own /search/suggest.json, so results are the store's real
 * index — no third-party service and nothing to keep in sync.
 *
 * Ordering follows SiteHeader.dc.html: brands first, then products with vendor
 * and price, then "See all results". Brands come from the per-vendor
 * `brand-<slug>` collections, remapped to their /pages/brand/<slug> metaobject
 * page so the customer lands on the brand's own page, not a bare collection.
 *
 * Progressive: the form still submits to /search if this never runs.
 */

const DEBOUNCE_MS = 200;
const LIMIT = 6;

class BunchberryPredictiveSearch extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('[data-bb-search-input]');
    this.panel = this.querySelector('[data-bb-search-panel]');
    if (!this.input || !this.panel) return;

    this.index = -1;
    this.controller = null;

    this.input.addEventListener('input', () => this.#schedule());
    this.input.addEventListener('focus', () => { if (this.input.value.trim()) this.#schedule(); });
    this.input.addEventListener('keydown', (e) => this.#key(e));

    // Close on outside click or Escape, but not when moving into the panel.
    document.addEventListener('click', (e) => {
      if (!this.contains(e.target)) this.#close();
    });
  }

  #schedule() {
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length < 2) return this.#close();
    this.timer = setTimeout(() => this.#fetch(q), DEBOUNCE_MS);
  }

  async #fetch(q) {
    // Abandon a slower in-flight request so results can't arrive out of order.
    this.controller?.abort();
    this.controller = new AbortController();

    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json`
      + `?q=${encodeURIComponent(q)}`
      + `&resources[type]=product,collection`
      + `&resources[limit]=${LIMIT}`
      + `&resources[options][unavailable_products]=last`;

    try {
      const res = await fetch(url, { signal: this.controller.signal });
      if (!res.ok) return this.#close();
      const data = await res.json();
      const results = data?.resources?.results ?? {};
      results.products = rankByAllTerms(q, results.products ?? []);
      this.#render(q, results);
    } catch (e) {
      if (e.name !== 'AbortError') this.#close();
    }
  }

  #render(q, results) {
    const products = results.products ?? [];
    const collections = results.collections ?? [];

    // `brand-<slug>` collections are brands; everything else is a category.
    const brands = collections.filter((c) => c.handle?.startsWith('brand-'));
    const categories = collections.filter((c) => !c.handle?.startsWith('brand-'));

    const rows = [];

    for (const b of brands.slice(0, 3)) {
      rows.push(
        `<a class="bb-sug__row" href="${esc(b.url)}" role="option">
           <span class="bb-sug__initial" aria-hidden="true">${esc((b.title || '?')[0])}</span>
           <span class="bb-sug__name">${esc(b.title)}</span>
           <span class="bb-sug__meta">brand</span>
         </a>`
      );
    }

    for (const c of categories.slice(0, 2)) {
      rows.push(
        `<a class="bb-sug__row" href="${esc(c.url)}" role="option">
           <span class="bb-sug__initial" aria-hidden="true">#</span>
           <span class="bb-sug__name">${esc(c.title)}</span>
           <span class="bb-sug__meta">category</span>
         </a>`
      );
    }

    for (const p of products.slice(0, LIMIT)) {
      const img = p.featured_image?.url
        ? `<img class="bb-sug__thumb" src="${esc(p.featured_image.url)}" alt="" loading="lazy">`
        : `<span class="bb-sug__thumb" aria-hidden="true"></span>`;
      const vendor = p.vendor ? ` <span class="bb-sug__by">by ${esc(p.vendor)}</span>` : '';
      rows.push(
        `<a class="bb-sug__row" href="${esc(p.url)}" role="option">
           ${img}
           <span class="bb-sug__title">${esc(p.title)}${vendor}</span>
           <span class="bb-sug__price">${esc(p.price ? formatMoney(p.price) : '')}</span>
         </a>`
      );
    }

    if (!rows.length) {
      rows.push(`<span class="bb-sug__empty">No match yet — we add Canadian brands weekly.</span>`);
    }

    rows.push(
      `<a class="bb-sug__all" href="/search?q=${encodeURIComponent(q)}" role="option">See all results →</a>`
    );

    this.panel.innerHTML = rows.join('');
    this.panel.hidden = false;
    this.input.setAttribute('aria-expanded', 'true');
    this.index = -1;
  }

  #key(e) {
    const items = [...this.panel.querySelectorAll('a')];
    if (e.key === 'Escape') return this.#close();
    if (!items.length || this.panel.hidden) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      this.index += e.key === 'ArrowDown' ? 1 : -1;
      if (this.index < 0) this.index = items.length - 1;
      if (this.index >= items.length) this.index = 0;
      items.forEach((el, i) => el.classList.toggle('is-active', i === this.index));
      items[this.index].scrollIntoView({ block: 'nearest' });
    } else if (e.key === 'Enter' && this.index >= 0) {
      // Only hijack Enter once something is highlighted; otherwise submit.
      e.preventDefault();
      items[this.index].click();
    }
  }

  #close() {
    this.panel.hidden = true;
    this.panel.innerHTML = '';
    this.input.setAttribute('aria-expanded', 'false');
    this.index = -1;
  }
}

function esc(s) {
  return String(s ?? '').replace(/[&<>"']/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]
  );
}

/**
 * Re-rank so products matching EVERY word of the query come first.
 *
 * suggest.json scores each term independently, so a two-word query is carried
 * by whichever word is common. Searching "black tea" returns the teas that
 * merely match "tea", because "tea" appears in fifty titles and the ranking has
 * no notion that both words were asked for.
 *
 * This is a re-order, never a filter: partial matches keep their place behind
 * the full ones, so a query with no complete match still returns the same set
 * rather than an empty box.
 */
function rankByAllTerms(query, products) {
  const terms = query.toLowerCase().split(/\s+/).filter((t) => t.length > 1);
  if (terms.length < 2) return products;

  // Only what a shopper can see on the card. Descriptions are what pull
  // chai into a search for black tea: the ingredient list mentions it.
  const haystack = (p) =>
    [p.title, p.vendor, p.product_type, ...(p.tags ?? [])]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

  return products
    .map((p, i) => {
      const hay = haystack(p);
      const hits = terms.filter((t) => hay.includes(t)).length;
      // Whole-phrase matches rank above merely having all the words apart.
      const phrase = hay.includes(terms.join(' ')) ? 1 : 0;
      return { p, i, hits, phrase };
    })
    .sort((a, b) =>
      b.phrase - a.phrase || b.hits - a.hits || a.i - b.i
    )
    .map((x) => x.p);
}

/** suggest.json returns a plain decimal string; render it the way the store does. */
function formatMoney(value) {
  const n = Number(value);
  if (Number.isNaN(n)) return String(value);
  return `$${n.toFixed(2)}`;
}

if (!customElements.get('bunchberry-predictive-search')) {
  customElements.define('bunchberry-predictive-search', BunchberryPredictiveSearch);
}
